(() => {
  "use strict";

  const SELECTOR = "[data-velora-scroll-hero]";
  const FRAME_STEP_MOBILE = 2;
  const LOAD_CONCURRENCY = 6;

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const map = (value, inStart, inEnd) => clamp((value - inStart) / (inEnd - inStart));
  const ease = value => 1 - Math.pow(1 - clamp(value), 3);
  const smoothstep = value => {
    const x = clamp(value);
    return x * x * (3 - 2 * x);
  };

  function init(root) {
    if (!root || root.dataset.veloraInitialized === "true") return;
    root.dataset.veloraInitialized = "true";

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const saveData = Boolean(navigator.connection && navigator.connection.saveData);

    if (reducedMotion || saveData) {
      root.classList.add("is-static");
      return;
    }

    const story = root.querySelector("[data-velora-story]");
    const canvas = root.querySelector("[data-velora-canvas]");
    const sourceNode = root.querySelector("[data-velora-frame-sources]");
    if (!story || !canvas || !sourceNode) return;

    let sources;
    try {
      sources = JSON.parse(sourceNode.textContent);
    } catch (error) {
      root.classList.add("is-static");
      return;
    }

    if (!Array.isArray(sources) || sources.length === 0) {
      root.classList.add("is-static");
      return;
    }

    const ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });
    if (!ctx) {
      root.classList.add("is-static");
      return;
    }

    const intro = root.querySelector("[data-velora-intro]");
    const introInner = root.querySelector("[data-velora-intro-inner]");
    const scrollCue = root.querySelector("[data-velora-scroll-cue]");
    const shade = root.querySelector("[data-velora-shade]");
    const chapter = root.querySelector("[data-velora-chapter]");
    const finale = root.querySelector("[data-velora-finale]");
    const finaleInner = root.querySelector("[data-velora-finale-inner]");
    const header = root.querySelector("[data-velora-header]");
    const headerChapter = root.querySelector("[data-velora-header-chapter]");
    const headerIndex = root.querySelector("[data-velora-header-index]");
    const loading = root.querySelector("[data-velora-loading]");
    const loadingBar = root.querySelector("[data-velora-loading-bar]");
    const loadingLabel = root.querySelector("[data-velora-loading-label]");

    const frameCount = sources.length;
    const isMobile = window.matchMedia("(max-width: 700px)").matches;
    const step = isMobile ? FRAME_STEP_MOBILE : 1;
    const allowed = Array.from({ length: frameCount }, (_, index) => index)
      .filter(index => index === frameCount - 1 || index % step === 0);
    const allowedSet = new Set(allowed);
    const images = new Array(frameCount);
    const states = new Array(frameCount).fill("idle");
    const queue = [];
    const queued = new Set();

    let activeLoads = 0;
    let settled = 0;
    let canvasWidth = 0;
    let canvasHeight = 0;
    let current = 0;
    let target = 0;
    let raf = 0;
    let lastFrame = -1;
    let destroyed = false;
    let resizeTimer = 0;
    let releaseTimer = 0;

    const INTRO_END = 0.17;
    const VIDEO_END = 0.84;
    const priority = [0, 1, 2, 3, 4, 6, 8, 12, 20, 40, 60, 80, frameCount - 1]
      .map(index => nearestAllowedIndex(index))
      .filter((index, position, list) => list.indexOf(index) === position);

    function nearestAllowedIndex(index) {
      const safe = Math.round(clamp(index, 0, frameCount - 1));
      if (allowedSet.has(safe)) return safe;
      for (let offset = 1; offset < frameCount; offset += 1) {
        if (allowedSet.has(safe - offset)) return safe - offset;
        if (allowedSet.has(safe + offset)) return safe + offset;
      }
      return 0;
    }

    function releaseLoading() {
      if (!loading || loading.classList.contains("is-complete")) return;
      loading.classList.add("is-complete");
      if (loadingLabel) loadingLabel.textContent = "Experience bereit";
    }

    function updateLoading() {
      const readyTarget = Math.min(12, allowed.length);
      const progress = clamp(settled / readyTarget);
      if (loadingBar) loadingBar.style.transform = `scaleX(${progress})`;
      if (settled >= readyTarget) releaseLoading();
    }

    function settle(index, image) {
      activeLoads -= 1;
      settled += 1;
      states[index] = image.naturalWidth ? "loaded" : "error";
      updateLoading();
      if (image.naturalWidth && lastFrame < 0) requestTick();
      pumpQueue();
    }

    function load(index, highPriority = false) {
      const safeIndex = nearestAllowedIndex(index);
      if (states[safeIndex] !== "idle" || queued.has(safeIndex)) return;
      queued.add(safeIndex);
      if (highPriority) queue.unshift(safeIndex);
      else queue.push(safeIndex);
      pumpQueue();
    }

    function pumpQueue() {
      if (destroyed) return;

      while (activeLoads < LOAD_CONCURRENCY && queue.length) {
        const index = queue.shift();
        queued.delete(index);
        if (states[index] !== "idle") continue;

        states[index] = "loading";
        activeLoads += 1;
        const image = new Image();
        image.decoding = "async";
        image.onload = () => settle(index, image);
        image.onerror = () => settle(index, image);
        image.src = sources[index];
        images[index] = image;
      }
    }

    function loadBackgroundFrames() {
      const remaining = allowed.filter(index => !priority.includes(index));
      remaining.forEach(index => load(index));
    }

    function sizeCanvas() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvasWidth = Math.max(1, Math.round(canvas.clientWidth * dpr));
      canvasHeight = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width !== canvasWidth) canvas.width = canvasWidth;
      if (canvas.height !== canvasHeight) canvas.height = canvasHeight;
      lastFrame = -1;
    }

    function nearestLoadedFrame(index) {
      const safeIndex = nearestAllowedIndex(index);
      const exact = images[safeIndex];
      if (exact && states[safeIndex] === "loaded") return exact;

      for (let offset = 1; offset < frameCount; offset += 1) {
        const lower = safeIndex - offset;
        const upper = safeIndex + offset;
        if (lower >= 0 && images[lower] && states[lower] === "loaded") return images[lower];
        if (upper < frameCount && images[upper] && states[upper] === "loaded") return images[upper];
      }
      return null;
    }

    function ensureNearby(index) {
      const safeIndex = nearestAllowedIndex(index);
      [safeIndex, safeIndex - step, safeIndex + step, safeIndex - step * 2, safeIndex + step * 2]
        .filter(candidate => candidate >= 0 && candidate < frameCount)
        .forEach(candidate => load(candidate, true));
    }

    function drawFrame(index) {
      ensureNearby(index);
      const image = nearestLoadedFrame(index);
      if (!image) return;

      const scale = Math.max(canvasWidth / image.naturalWidth, canvasHeight / image.naturalHeight);
      const width = image.naturalWidth * scale;
      const height = image.naturalHeight * scale;
      const x = (canvasWidth - width) / 2;
      const y = (canvasHeight - height) / 2;

      ctx.fillStyle = "#eee8e2";
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      ctx.drawImage(image, x, y, width, height);
    }

    function getProgress() {
      const rect = story.getBoundingClientRect();
      const distance = rect.height - window.innerHeight;
      return distance <= 0 ? 0 : clamp(-rect.top / distance);
    }

    function apply(progress) {
      const introExit = smoothstep(map(progress, .035, INTRO_END));
      const videoProgress = map(progress, INTRO_END - .015, VIDEO_END);
      const finaleProgress = ease(map(progress, VIDEO_END - .015, .965));
      const frameIndex = nearestAllowedIndex(Math.round(videoProgress * (frameCount - 1)));

      if (frameIndex !== lastFrame) {
        drawFrame(frameIndex);
        lastFrame = frameIndex;
      }

      if (intro) {
        intro.style.opacity = String(1 - map(progress, .105, INTRO_END));
        intro.style.clipPath = `inset(0 0 ${introExit * 100}% 0)`;
      }
      if (introInner) {
        introInner.style.opacity = String(1 - map(progress, .025, .125));
        introInner.style.transform = `translateY(${-1.5 - introExit * 12}vh) scale(${1 - introExit * .035})`;
      }
      if (scrollCue) scrollCue.style.opacity = String(1 - map(progress, .005, .055));

      const activeVideo = smoothstep(map(progress, INTRO_END - .02, INTRO_END + .05));
      if (shade) shade.style.opacity = String(activeVideo * (1 - finaleProgress * .55));
      canvas.style.transform = `scale(${1.012 + videoProgress * .018})`;

      if (chapter) {
        const labelIn = smoothstep(map(progress, INTRO_END + .02, INTRO_END + .075));
        const labelOut = 1 - smoothstep(map(progress, VIDEO_END - .12, VIDEO_END - .035));
        chapter.style.opacity = String(labelIn * labelOut);
        chapter.style.transform = `translateY(${(1 - labelIn) * 12}px)`;
      }

      if (finale) {
        finale.style.opacity = String(finaleProgress);
        finale.style.setProperty("--velora-finale-scrim", String(finaleProgress));
        finale.style.pointerEvents = finaleProgress > .8 ? "auto" : "none";
      }
      if (finaleInner) finaleInner.style.transform = `translateY(${(1 - finaleProgress) * 30}px)`;

      if (header) {
        header.classList.toggle("is-light", progress > .125);
        header.style.setProperty("--velora-header-progress", progress.toFixed(4));
      }
      if (headerIndex) headerIndex.textContent = `${String(Math.round(progress * 100)).padStart(2, "0")} / 100`;
      if (headerChapter) {
        if (progress < INTRO_END - .01) headerChapter.textContent = "Choose by love";
        else if (progress < VIDEO_END - .06) headerChapter.textContent = "From nature to home";
        else headerChapter.textContent = "Signature Box";
      }
    }

    function tick() {
      raf = 0;
      current += (target - current) * .14;
      if (Math.abs(target - current) < .00015) current = target;
      apply(current);
      if (current !== target) requestTick();
    }

    function requestTick() {
      if (!raf && !destroyed) raf = window.requestAnimationFrame(tick);
    }

    function onScroll() {
      target = getProgress();
      requestTick();
    }

    function onResize() {
      sizeCanvas();
      target = current = getProgress();
      apply(current);
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        lastFrame = -1;
        requestTick();
      }, 120);
    }

    function destroy() {
      destroyed = true;
      window.cancelAnimationFrame(raf);
      window.clearTimeout(resizeTimer);
      window.clearTimeout(releaseTimer);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      window.removeEventListener("pageshow", onResize);
      root.dataset.veloraInitialized = "false";
    }

    root.veloraDestroy = destroy;
    sizeCanvas();
    priority.forEach(index => load(index, true));
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(loadBackgroundFrames, { timeout: 1200 });
    } else {
      window.setTimeout(loadBackgroundFrames, 240);
    }
    releaseTimer = window.setTimeout(releaseLoading, 2400);
    target = current = getProgress();
    apply(current);

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    window.addEventListener("pageshow", onResize);
  }

  function initAll(scope = document) {
    if (scope.matches && scope.matches(SELECTOR)) init(scope);
    scope.querySelectorAll(SELECTOR).forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", event => initAll(event.target));
  document.addEventListener("shopify:section:unload", event => {
    const root = event.target.matches(SELECTOR) ? event.target : event.target.querySelector(SELECTOR);
    if (root && typeof root.veloraDestroy === "function") root.veloraDestroy();
  });
})();


(() => {
  const root = document.querySelector("[data-velora-scroll-hero]");
  const story = root?.querySelector("[data-velora-story]");
  if (!root || !story) return;

  document.body.classList.add("velora-scroll-hero-active");

  const updateHeader = () => {
    if (root.classList.contains("is-static")) {
      document.body.classList.add("velora-scroll-hero-complete");
      return;
    }

    const rect = story.getBoundingClientRect();
    const distance = rect.height - window.innerHeight;
    const progress =
      distance <= 0
        ? 0
        : Math.min(1, Math.max(0, -rect.top / distance));

    document.body.classList.toggle(
      "velora-scroll-hero-complete",
      progress >= 0.84
    );
  };

  updateHeader();
  window.addEventListener("scroll", updateHeader, { passive: true });
  window.addEventListener("resize", updateHeader);
})();